// Reusable chip-style multi-tag input.
//
// Used by SettingsPage's Machines / Vendors / Materials tabs so Justin can
// type tags like "mill" / "lathe" / "finishing" against each item. Tags are
// freeform (no enum), and the first tag drives the default grouping.
//
// Behavior:
//   - Existing tags render as orange pills with × to remove
//   - Input field accepts text; Enter or comma commits the tag
//   - Empty / whitespace-only tags are dropped
//   - Duplicate tags (case-insensitive) are not added a second time
//   - Suggestions (existing tags from siblings) drop down below the input
//     when focused, filtered against current input

import { useMemo, useRef, useState } from 'react';

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  /** Existing tags from siblings, used to power the suggestion dropdown. */
  suggestions?: string[];
}

export function TagInput({
  value,
  onChange,
  placeholder = 'Add a tag (mill, lathe, …)',
  suggestions = [],
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function commitTag(raw: string) {
    const trimmed = raw.trim();
    if (!trimmed) return;
    // Case-insensitive duplicate check so "Mill" + "mill" doesn't double up.
    const lower = trimmed.toLowerCase();
    if (value.some((t) => t.toLowerCase() === lower)) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  }

  function removeTag(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      // Quick-delete the last chip on Backspace when input is empty.
      removeTag(value.length - 1);
    }
  }

  // Filter suggestions by draft (case-insensitive) and exclude already-picked.
  const filteredSuggestions = useMemo(() => {
    const picked = new Set(value.map((t) => t.toLowerCase()));
    const needle = draft.trim().toLowerCase();
    return suggestions
      .filter((s) => !picked.has(s.toLowerCase()))
      .filter((s) => (needle ? s.toLowerCase().includes(needle) : true))
      .slice(0, 8);
  }, [suggestions, value, draft]);

  return (
    <div className="relative">
      <div
        className="flex flex-wrap items-center gap-1 px-2 py-1 border border-line-dark rounded bg-white focus-within:border-orange min-h-[34px]"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange text-white text-xs rounded-full font-semibold"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(i);
              }}
              className="text-white/80 hover:text-white text-base leading-none"
              aria-label={`Remove tag ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          // Delay blur so a click on a suggestion can register before the
          // dropdown hides.
          onBlur={() => window.setTimeout(() => setFocused(false), 120)}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] px-1 py-0.5 text-sm border-none outline-none bg-transparent"
        />
      </div>
      {focused && filteredSuggestions.length > 0 && (
        <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-line-dark rounded shadow-md max-h-48 overflow-y-auto">
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              // onMouseDown fires before the input's onBlur — important so we
              // commit before the dropdown hides.
              onMouseDown={(e) => {
                e.preventDefault();
                commitTag(s);
              }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-bg"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
